/**
 * UploadPlan — the customer's stated intent for what they're about to upload,
 * collected by the Upload Guide (PRODUCT "Upload Guide" / "Upload plan").
 *
 * It is purely for the customer's benefit: it shapes how the rest of the flow is
 * framed. It is **persisted in the database, one record per upload session**
 * (`upload_plans`): a new upload duplicates the org's most-recent plan, and the
 * guide edits this session's record — so the latest plan becomes the default for the
 * next upload. The vocabulary lives here in `@repo/shared/common` (not the app) so
 * the Zod wire contract (`contracts/uploads.ts`) derives its enums from these `const`
 * arrays — anti-drift, the same habit as the field catalog.
 */

/**
 * Where the customer's origination data lives.
 *   - `in_snapshots`  — origination fields are columns in the snapshot rows.
 *   - `separate_file` — origination data arrives as its own file.
 *   - `estimated`     — not available; the product estimates origination date from
 *                       the first month a loan appears and original balance from the
 *                       first balance seen.
 */
export const ORIGINATION_DATA_OPTIONS = [
  'in_snapshots',
  'separate_file',
  'estimated',
] as const;
export type OriginationDataOption = (typeof ORIGINATION_DATA_OPTIONS)[number];

/**
 * Where the customer's charge-off events live (drive credit-loss modeling).
 *   - `in_snapshots`  — charge-off signal is in the snapshot rows.
 *   - `separate_file` — charge-offs arrive as a separate transactions file.
 */
export const CHARGE_OFF_OPTIONS = ['in_snapshots', 'separate_file'] as const;
export type ChargeOffOption = (typeof CHARGE_OFF_OPTIONS)[number];

/**
 * How the customer's data lets the product detect prepayment (partial + full).
 *   - `amount_in_snapshots` — snapshots show the prepayment amount directly.
 *   - `expected_vs_actual`  — snapshots show expected vs. actual payment.
 *
 * There is deliberately NO "amortization attributes" option: the product never
 * reconstructs an amortization schedule (from balances, a rate, or a payment
 * amount) to infer prepayment (WS-A Method-C removal / RB-4, 2026-07-16). Either
 * way, measuring prepayment SPEED also needs reported Scheduled Principal (due or
 * paid) — the guide's prepayment step says so plainly (RB-8).
 */
export const PREPAYMENT_OPTIONS = ['amount_in_snapshots', 'expected_vs_actual'] as const;
export type PrepaymentOption = (typeof PREPAYMENT_OPTIONS)[number];

/**
 * The customer's stated upload plan — the three guide decisions, plus the implicit
 * "snapshots" baseline they always provide. Persisted per upload session.
 */
export interface UploadPlan {
  readonly originationData: OriginationDataOption;
  readonly chargeOff: ChargeOffOption;
  readonly prepayment: PrepaymentOption;
}

/**
 * The safe / most-common defaults — the seed for a first-ever plan (no prior plan to
 * duplicate): everything the customer is most likely to have lives in the snapshots,
 * with the amount-based prepayment signal (the simplest to provide).
 */
export const DEFAULT_UPLOAD_PLAN: UploadPlan = {
  originationData: 'in_snapshots',
  chargeOff: 'in_snapshots',
  prepayment: 'amount_in_snapshots',
};

/**
 * Runtime guard for an untrusted value being a valid {@link UploadPlan}. Derived from
 * the option `const` arrays so it can never drift from the vocabulary above. Used at
 * the persistence/wire boundary.
 */
export function isUploadPlan(value: unknown): value is UploadPlan {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  const inSet = (set: readonly string[], x: unknown): boolean =>
    typeof x === 'string' && set.includes(x);
  return (
    inSet(ORIGINATION_DATA_OPTIONS, v.originationData) &&
    inSet(CHARGE_OFF_OPTIONS, v.chargeOff) &&
    inSet(PREPAYMENT_OPTIONS, v.prepayment)
  );
}

/**
 * Legacy → surviving option mapping for values a stored plan may still carry from
 * a release where the option existed. `amortization_attributes` (removed by the
 * WS-A Method-C removal) maps to `expected_vs_actual` — the nearest surviving
 * schedule-based intent (the same target as the one-shot `upload_plans` data
 * migration; the guide re-asks on the next pass either way).
 */
const LEGACY_PREPAYMENT_MAP: Readonly<Record<string, PrepaymentOption>> = {
  amortization_attributes: 'expected_vs_actual',
};

/** Decode one plan field tolerantly: member → itself, known-legacy → mapped, else default. */
function coerceOption<T extends string>(
  set: readonly T[],
  value: unknown,
  fallback: T,
  legacy?: Readonly<Record<string, T>>,
): T {
  if (typeof value === 'string') {
    if ((set as readonly string[]).includes(value)) return value as T;
    const mapped = legacy?.[value];
    if (mapped !== undefined) return mapped;
  }
  return fallback;
}

/**
 * Tolerant decode of an untrusted/stored plan value into a valid {@link UploadPlan}.
 * Unlike {@link isUploadPlan} (a strict guard for the wire boundary), this never
 * throws or rejects: an unknown or legacy enum member degrades to the mapped
 * surviving option or the safe default, so a plan persisted before an option was
 * removed can't crash the guide or the resolve endpoint's outbound validation.
 */
export function coerceUploadPlan(value: unknown): UploadPlan {
  const v =
    typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
  return {
    originationData: coerceOption(
      ORIGINATION_DATA_OPTIONS,
      v['originationData'],
      DEFAULT_UPLOAD_PLAN.originationData,
    ),
    chargeOff: coerceOption(CHARGE_OFF_OPTIONS, v['chargeOff'], DEFAULT_UPLOAD_PLAN.chargeOff),
    prepayment: coerceOption(
      PREPAYMENT_OPTIONS,
      v['prepayment'],
      DEFAULT_UPLOAD_PLAN.prepayment,
      LEGACY_PREPAYMENT_MAP,
    ),
  };
}
